import { useMemo } from 'react';

const EMPTY_ARRAY = [];

// Reference's Expansion Score table blanks out these specific reps (internal
// / house accounts, not real customer-facing sales reps) to an em dash.
const EXCLUDED_REPS = new Set([
  'Adam Crunkleton', 'Kevin Brown', 'Matt Bitzer', 'Jon Mills', 'Talton Taylor',
  'Michael Granat', 'Kavish Engineer', 'Jameson Jackson', 'Albert Lee',
]);

const rowMonthArr = (row, month) => Number(row?.monthly_arr?.[month] || 0);

const moneyShort = (value) => {
  const n = Number(value || 0);
  const a = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (a >= 1e6) return `${sign}$${(a / 1e6).toFixed(2)}M`;
  if (a >= 1e3) return `${sign}$${(a / 1e3).toFixed(1)}K`;
  return `${sign}$${Math.round(a).toLocaleString()}`;
};

// Exact port of the reference's fF() — whole-dollar money, no decimals ever.
const fF = (value) => {
  const n = Number(value || 0);
  return `${n < 0 ? '-' : ''}$${Math.abs(Math.round(n)).toLocaleString('en-US')}`;
};

function changeValue(changes, key) {
  const names = {
    upsell: ['Upsell', 'upsell'],
  }[key] || [key];
  return names.reduce((sum, name) => sum + Number(changes?.[name] || 0), 0);
}

// Matches the reference's Chart.js grouped bar chart exactly: two bars per
// cohort year (Started / Still Active), not the row-per-year layout this
// used to render.
function LogoRetentionChart({ rows }) {
  if (!rows.length) return <p style={{ color: '#94a3b8', fontSize: 13, textAlign: 'center', padding: '32px 0' }}>No data available.</p>;

  const maxV = Math.max(1, ...rows.flatMap(row => [row.started, row.active]));
  const PAD_L = 36;
  const PAD_R = 16;
  const PAD_T = 16;
  const PAD_B = 32;
  const SVG_H = 260;
  const CHART_H = SVG_H - PAD_T - PAD_B;
  const n = rows.length;
  const MIN_SLOT = 84;
  const SVG_W = Math.max(460, PAD_L + PAD_R + n * MIN_SLOT);
  const slot = (SVG_W - PAD_L - PAD_R) / n;
  const groupW = Math.min(64, slot * 0.62);
  const barW = groupW / 2 - 2;
  const baseY = PAD_T + CHART_H;
  const toY = (v) => PAD_T + CHART_H - (Number(v || 0) / maxV) * CHART_H;

  const TICKS = 5;
  const yTicks = Array.from({ length: TICKS + 1 }, (_, i) => {
    const v = (maxV / TICKS) * i;
    return { v, y: toY(v) };
  });

  return (
    <div style={{ overflowX: 'auto' }}>
      <svg viewBox={`0 0 ${SVG_W} ${SVG_H}`} style={{ width: '100%', minWidth: SVG_W, height: SVG_H, display: 'block' }}>
        {yTicks.map((tick, i) => (
          <g key={i}>
            <line x1={PAD_L} x2={SVG_W - PAD_R} y1={tick.y} y2={tick.y} stroke="rgba(0,0,0,.04)" strokeWidth="1" />
            <text x={PAD_L - 8} y={tick.y + 3.5} textAnchor="end" fontSize="9" fill="#8898aa" fontFamily="Inter, sans-serif">
              {Math.round(tick.v)}
            </text>
          </g>
        ))}
        <line x1={PAD_L} x2={PAD_L} y1={PAD_T} y2={baseY} stroke="#e2e8f0" strokeWidth="1" />
        <line x1={PAD_L} x2={SVG_W - PAD_R} y1={baseY} y2={baseY} stroke="#e2e8f0" strokeWidth="1" />
        {rows.map((row, i) => {
          const groupX = PAD_L + i * slot + (slot - groupW) / 2;
          const startedTop = toY(row.started);
          const activeTop = toY(row.active);
          return (
            <g key={row.year}>
              <rect x={groupX} y={startedTop} width={barW} height={Math.max(0, baseY - startedTop)}
                fill="rgba(30,95,168,.3)" stroke="#1e5fa8" strokeWidth="1" rx="4">
                <title>{`${row.year}\nStarted: ${row.started}`}</title>
              </rect>
              <rect x={groupX + barW + 4} y={activeTop} width={barW} height={Math.max(0, baseY - activeTop)}
                fill="rgba(21,128,61,.8)" stroke="#15803d" strokeWidth="1" rx="4">
                <title>{`${row.year}\nStill Active: ${row.active} / ${row.started} (${row.retention}%)`}</title>
              </rect>
              <text x={groupX + groupW / 2} y={baseY + 16} textAnchor="middle" fontSize="10" fill="#8898aa" fontFamily="Inter, sans-serif">
                {row.year}
              </text>
            </g>
          );
        })}
      </svg>
      <div className="arr-logo-retention-legend">
        <span><i style={{ background: 'rgba(30,95,168,.3)', borderColor: '#1e5fa8' }} />Started</span>
        <span><i style={{ background: 'rgba(21,128,61,.8)', borderColor: '#15803d' }} />Still Active</span>
      </div>
    </div>
  );
}

function IntelligenceTab({ data }) {
  const records = data.records || EMPTY_ARRAY;
  const trend = data.trend || EMPTY_ARRAY;

  const intelligence = useMemo(() => {
    const monthTotals = {};
    records.forEach(row => {
      Object.entries(row.monthly_arr || {}).forEach(([month, value]) => {
        monthTotals[month] = (monthTotals[month] || 0) + Number(value || 0);
      });
    });
    trend.forEach(row => {
      if (row.month && Number(row.value || 0) > 0) monthTotals[row.month] = Number(row.value || 0);
    });

    const allMonths = Object.keys(monthTotals).filter(month => Number(monthTotals[month] || 0) > 0).sort();
    // Anchor "latest" to the global filter bar's Period end date when set
    // (YTD/QTD/LTM all resolve "to" to the true latest month anyway; only
    // a Custom range with an earlier end actually shifts this), so
    // milestones/cohort/expansion all read "as of" the selected
    // period instead of always the absolute latest month with data.
    const latest = data.period?.to || allMonths.at(-1);
    const currentARR = Number(monthTotals[latest] || 0);

    const milestones = [5e6, 10e6, 15e6, 20e6, 25e6, 30e6, 40e6, 50e6, 75e6, 100e6].map(target => {
      const pct = Math.min(100, currentARR / target * 100);
      const done = currentARR >= target;
      const color = done ? '#15803d' : pct > 80 ? '#d97706' : '#1e5fa8';
      return { target, pct, done, color };
    });

    const custFirstYear = {};
    const custActive = {};
    const custInfo = {};
    records.forEach(row => {
      const customer = row.end_user || row.bill_to || 'Unspecified';
      const rep = row.sales_person || '';
      const activeMonths = Object.keys(row.monthly_arr || {}).filter(month => rowMonthArr(row, month) > 0).sort();
      if (activeMonths.length) {
        const year = activeMonths[0].slice(0, 4);
        if (!custFirstYear[customer] || year < custFirstYear[customer]) custFirstYear[customer] = year;
      }
      if (rowMonthArr(row, latest) > 0) custActive[customer] = 1;
      // Reference sets rep once from the first deal record encountered for
      // this customer and never revisits it on later records — matched here
      // rather than backfilling from a later, possibly-more-complete record.
      if (!custInfo[customer]) custInfo[customer] = { customer, rep, firstM: '', arr: 0, totalUp: 0, upMonths: 0 };
      if (activeMonths.length && (!custInfo[customer].firstM || activeMonths[0] < custInfo[customer].firstM)) {
        custInfo[customer].firstM = activeMonths[0];
      }
      custInfo[customer].arr += rowMonthArr(row, latest);
      Object.values(row.monthly_changes || {}).forEach(change => {
        const upsell = changeValue(change, 'upsell');
        if (upsell > 0) {
          custInfo[customer].totalUp += upsell;
          custInfo[customer].upMonths += 1;
        }
      });
    });

    const cohortMap = {};
    Object.keys(custFirstYear).forEach(customer => {
      const year = custFirstYear[customer];
      if (!cohortMap[year]) cohortMap[year] = { year, started: 0, active: 0 };
      cohortMap[year].started += 1;
      if (custActive[customer]) cohortMap[year].active += 1;
    });
    const logoRetention = Object.values(cohortMap).sort((a, b) => a.year.localeCompare(b.year)).map(row => ({
      ...row,
      retention: row.started > 0 ? Number((row.active / row.started * 100).toFixed(1)) : 0,
    }));

    const maxARR = Math.max(1, ...Object.values(custInfo).map(row => Number(row.arr || 0)));
    const latestIndex = allMonths.indexOf(latest);
    const expansion = Object.values(custInfo).filter(row => row.arr > 0 && row.totalUp === 0 && row.firstM).map(row => {
      const months = latestIndex - allMonths.indexOf(row.firstM);
      const tenureScore = Math.min(50, Math.round(months / 36 * 50));
      const arrScore = Math.min(50, Math.round(row.arr / maxARR * 50));
      const rep = EXCLUDED_REPS.has(row.rep) ? '—' : (row.rep || '—');
      return { ...row, rep, months, tenureScore, arrScore, score: tenureScore + arrScore };
    }).sort((a, b) => b.score - a.score).slice(0, 15);

    return {
      latest,
      currentARR,
      milestones,
      logoRetention,
      expansion,
    };
  }, [records, trend, data.period?.to]);

  return (
    <>
      <div className="arr-chart-card arr-t5-card">
        <h4 className="arr-t5-title">🎯 ARR Milestone Tracker</h4>
        <div className="arr-chart-sub">Progress toward next ARR milestones</div>
        <div className="arr-milestone-list">
          {intelligence.milestones.map(item => (
            <div className="arr-milestone-row" key={item.target}>
              <div className="arr-milestone-target">{moneyShort(item.target).replace('.00', '')}</div>
              <div className="arr-milestone-bar">
                <div className="arr-milestone-fill" style={{ width: `${item.pct}%`, background: item.color }}>
                  {item.pct > 15 ? moneyShort(intelligence.currentARR) : ''}
                </div>
              </div>
              <div className="arr-milestone-pct" style={{ color: item.color }}>{item.pct.toFixed(0)}%</div>
              <div className="arr-milestone-status">{item.done ? '✅' : '⬜'}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="arr-chart-card arr-t5-card">
        <h4 className="arr-t5-title">📊 Logo Retention by Cohort</h4>
        <div className="arr-chart-sub">% of customers retained by acquisition year</div>
        <LogoRetentionChart rows={intelligence.logoRetention} />
      </div>

      <div className="arr-chart-card arr-t5-card">
        <h4 className="arr-t5-title">🚀 Customer Expansion Opportunities</h4>
        <div className="arr-chart-sub">Customers ranked by expansion potential — tenure, ARR trend, expansion history</div>
        <div className="arr-expand-method">
          <b>📐 Score /100:</b> min(50, Tenure Months ÷ 36 × 50) + min(50, Customer ARR ÷ Max ARR × 50)
        </div>
        <div className="arr-expand-table-wrap">
          <table className="arr-expand-table">
            <thead>
              <tr>
                <th>Customer</th>
                <th>Rep</th>
                <th>Current ARR</th>
                <th>Tenure</th>
                <th title="Blue=Tenure points, Green=ARR points">Score Breakdown</th>
                <th title="Score out of 100: Tenure (50pts) + ARR size (50pts)">Score /100</th>
              </tr>
            </thead>
            <tbody>
              {intelligence.expansion.map(row => {
                const scoreClass = row.score >= 75 ? 'high' : row.score >= 40 ? 'mid' : 'low';
                return (
                  <tr key={row.customer}>
                    <td className="arr-expand-name">{row.customer}</td>
                    <td>{row.rep}</td>
                    <td className="arr-expand-money">{fF(row.arr)}</td>
                    <td>{row.months}m</td>
                    <td>
                      <div className="arr-expand-scorebar">
                        <i style={{ width: `${row.tenureScore}%`, background: '#1e5fa8' }} />
                        <i style={{ width: `${row.arrScore}%`, background: '#15803d' }} />
                        <i style={{ width: `${Math.max(0, 100 - row.tenureScore - row.arrScore)}%`, background: '#f1f5f9' }} />
                      </div>
                      <div className="arr-expand-score-note">{row.tenureScore}t + {row.arrScore}a</div>
                    </td>
                    <td><span className={`arr-score-pill ${scoreClass}`}>{row.score}</span></td>
                  </tr>
                );
              })}
              {!intelligence.expansion.length && <tr><td colSpan={6} className="bm-empty-row">No expansion candidates found</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

export default IntelligenceTab;
export { IntelligenceTab };
